import { Divider, FormHelperText, Grid, Stack, styled } from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { AttentionLabel } from '../components/common/attentionLabel'
import { BottomButtonGroup } from '../components/common/bottomButtonGroup'
import { BackButton } from '../components/common/buttons/backButton'
import { ButtonL } from '../components/common/buttons/buttonL'
import { ButtonOutlinedWarningS } from '../components/common/buttons/buttonOutlinedWarningS'
import { ButtonS } from '../components/common/buttons/buttonS'
import { GItem, GItemContainer, MiddleGItem } from '../components/common/grids'
import { ErrorMessageCard } from '../components/common/errorMessageCard'
import { Form } from '../components/common/inputs/form'
import { RadioButtonGroup } from '../components/common/inputs/radioButtonGroup'
import { TextBox } from '../components/common/inputs/textBox'
import { NumberBox } from '../components/common/inputs/numberBox'
import { SubTitle } from '../components/common/subTitle'
import { yesNo } from '../../containers/common/constant/classification'
import { isEditable } from '../../containers/common/category'
import { useAction } from '../../containers/memberModifyChildHospitalAdmission/memberModifyChildHospitalAdmissionService'
import { GetCategoryMstDto } from '../../dataAccess/webApi/dto/categoryMstDto'
import { translate } from '../../i18n'
import { getValueByNameStr } from '../../utils/objectUtil'

export const MemberModifyChildHospitalAdmission = () => {
  const {
    CATEGORY_KEY,
    SUB_CATEGORIES,
    formMethods,
    fields,
    hospitalAdmissionFlag,
    hospitalAdmissions,
    categoryMst,
    hasUsagePermission,
    onFieldAppend,
    onFieldRemove,
    onSubmit,
    childrenResultCode,
  } = useAction()

  let targetCategory: GetCategoryMstDto | undefined
  if (categoryMst) {
    targetCategory = categoryMst.find((mst) => mst.category === CATEGORY_KEY)
  }
  if (!targetCategory) return (<BackButton />)
  
  const hospitalAdmissionCategory = categoryMst?.find((value) => value.category === CATEGORY_KEY)
  const subCategories = categoryMst?.filter((mst) => SUB_CATEGORIES.includes(mst.category))
  
  if (!hospitalAdmissionCategory || !subCategories || subCategories.length === 0) return (<BackButton />)
  
  const subCategoryMap: {[key:string]: GetCategoryMstDto} = {}
  subCategories?.forEach(e => subCategoryMap[e.category.substring(CATEGORY_KEY.length + 1)] = e)
  
  return (
    <Form formMethods={formMethods} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <Grid container>
          <Grid item md={3}></Grid>
          <Grid container item xs={12} md={6} rowSpacing={1}>
            <Grid item xs={12}>
              <SubTitle title={hospitalAdmissionCategory.name} />
            </Grid>
            <Grid item xs={0.3}/>
            <Grid item xs={11.7}>
              <RadioButtonGroup
                name="hospitalAdmissionFlag"
                label={targetCategory.name}
                buttonValueLabels={[
                  { label: targetCategory.checkTextNo ?? 'なし', value: yesNo.no },
                  { label: targetCategory.checkTextYes ?? 'あり', value: yesNo.yes },
                ]}
                row
                disabled={!isEditable(targetCategory.editCitizen, hasUsagePermission)}
              />
            </Grid>
            <Grid item xs={0.3}/>
            <Grid item xs={11.7}>
              {hospitalAdmissionFlag === yesNo.yes && (
                <>
                  {
                    fields != null && fields.map((e, i) => {
                      if (hospitalAdmissions != null && hospitalAdmissions[i] != null && hospitalAdmissions[i]?.deleted) return null
                      return (
                        <InputForm
                          key={e.id}
                          subCategoryMap={subCategoryMap}
                          topName={`${CATEGORY_KEY}.${i}`}
                          onRemove={() => onFieldRemove(i)}
                          disabled={targetCategory ? !isEditable(targetCategory.editCitizen, hasUsagePermission) : false}
                        />
                      )
                    })
                  }
                  <ButtonS onClick={onFieldAppend} disabled={!isEditable(targetCategory.editCitizen, hasUsagePermission)}>
                    追加
                  </ButtonS>
                </>
              )}
            </Grid>
          </Grid>
          <Grid item md={3}></Grid>
        </Grid>

        {!!childrenResultCode && (
          <ErrorMessageCard
            messages={[translate(`memberModifyChildHospitalAdmission.error.childrenResultCode${childrenResultCode}`)]}
          />
        )}

        <BottomButtonGroup>
          <ButtonL fullWidth type="submit" disabled={!isEditable(targetCategory.editCitizen, hasUsagePermission)}>
            {translate('memberModifyChildHospitalAdmission.button.decision')}
          </ButtonL>
          <BackButton />
        </BottomButtonGroup>
      </Stack>
    </Form>
  )
}

interface InputFormProps {
  subCategoryMap: {[key:string]: GetCategoryMstDto}
  topName: string
  onRemove: () => void
  disabled: boolean
}

const ErrorText = styled(FormHelperText)({
  color: '#c53030',
  marginLeft: '14px',
})

const AdmissionDivider = styled(Divider)(() => ({
  marginBottom: "8px",
  marginTop: "8px",
}))

const ShortNumberBox = styled(NumberBox)(() => ({
  width: "32px",
}))
/** 各項目の入力フォーム */
const InputForm = (props: InputFormProps) => {
  const {subCategoryMap, topName, onRemove, disabled } = props
  const {admissionAge, reason} = subCategoryMap
  
  const { formState: { errors } } = useFormContext()
  const admissionAgeError = getValueByNameStr(errors, `${topName}.admissionAge`)
  const admissionMonthError = getValueByNameStr(errors, `${topName}.admissionMonth`)
  const render = () => {
    return (
      <>
        {admissionAge != null && <GItemContainer xs={12} rowSpacing={1}>
          <MiddleGItem xs={5} sm={2}>
            {admissionAge.required === yesNo.yes && (
              <AttentionLabel>*</AttentionLabel>
            )}
            {admissionAge.altCategoryName ?? admissionAge.name}
          </MiddleGItem>
          <GItem xs={7} sm={10}>
            <ShortNumberBox 
              name={`${topName}.admissionAge`}
              label="年齢"
              min={0}
              max={12}
              maxLength={admissionAge.maxLength}
              required={admissionAge.required === yesNo.yes}
              disabled={disabled}
            />
            {translate('memberModifyChildHospitalAdmission.label.age')}
            <ShortNumberBox 
              name={`${topName}.admissionMonth`}
              label="月齢"
              min={0}
              max={12}
              maxLength={admissionAge.maxLength}
              required={admissionAge.required === yesNo.yes}
              disabled={disabled}
            />
            {translate('memberModifyChildHospitalAdmission.label.month')}
          </GItem>
          <Grid>
            {admissionAgeError && <ErrorText>{admissionAgeError.message}</ErrorText>}
            {admissionMonthError && <ErrorText>{admissionMonthError.message}</ErrorText>}
          </Grid>
        </GItemContainer>}
        
        {reason != null && <GItemContainer xs={12} rowSpacing={1}>
          <MiddleGItem xs={5} sm={2}>
            <GItemContainer xs={12}>
              <GItem xs={12}>
                <div>
                  {reason.required === yesNo.yes && (
                    <AttentionLabel>*</AttentionLabel>
                  )}
                  {reason.name}
                </div>
              </GItem>
            </GItemContainer>
          </MiddleGItem>
          <GItem xs={7} sm={10}>
            <TextBox
              label={reason.name}
              name={`${topName}.reason`}
              maxLength={reason.maxLength ?? 200}
              required={reason.required === yesNo.yes}
              disabled={disabled}
            />
          </GItem>
        </GItemContainer>}
        
        <ButtonOutlinedWarningS onClick={onRemove} disabled={disabled}>
          削除
        </ButtonOutlinedWarningS>
        {<AdmissionDivider />}
      </>
    )
  }
  
  return (render())
}